/**
 * Fila de leituras por gravar (Fase 2c, L4; correção 9). Puro — sem React —
 * para se testar com `pnpm test`; o hook `usePendingQueue` usa-o.
 *
 * **Uma leitura nunca se perde:**
 * - grava-se logo; sem rede (ou 5xx, 408, 429, 401, ou um 409 de tranca —
 *   «a ser aplicada», «a ser confirmado») fica na fila, marcada, e repete-se
 *   em silêncio;
 * - uma **recusa** (outro 4xx) não se apaga: vai para «Não gravadas», com a
 *   razão, até a pessoa a tentar de novo ou a descartar.
 *
 * Com `serial`, os envios saem um de cada vez (leituras do mesmo frame em fila).
 */

export interface PendingItem<P> {
  key: string
  payload: P
  /** Tentou e falhou (rede): mostra-se marcada. */
  failed: boolean
  at: string
}

export interface RejectedItem<P> {
  key: string
  payload: P
  reason: string
  at: string
}

export type SendOutcome<R> = { ok: true; result: R } | { ok: false; queued: true } | { ok: false; queued: false; error: Error }

export interface QueueStorage {
  get(key: string): string | null
  set(key: string, value: string): void
  remove(key: string): void
}

/** `localStorage` quando existe; senão (modo privado, testes) só memória. */
export function browserStorage(): QueueStorage {
  const mem = new Map<string, string>()
  const ls = (() => {
    try {
      return typeof localStorage !== "undefined" ? localStorage : null
    } catch {
      return null
    }
  })()
  return {
    get: (k) => {
      try {
        return ls ? ls.getItem(k) : mem.get(k) ?? null
      } catch {
        return mem.get(k) ?? null
      }
    },
    set: (k, v) => {
      try {
        if (ls) ls.setItem(k, v)
        else mem.set(k, v)
      } catch {
        mem.set(k, v)
      }
    },
    remove: (k) => {
      try {
        if (ls) ls.removeItem(k)
        else mem.delete(k)
      } catch {
        mem.delete(k)
      }
    },
  }
}

/** Erro com o estado HTTP (sem estado = rede). Espelha `RequestError`. */
interface WithStatus {
  status?: number | null
  message?: string
}

/** Tranca de outro pedido (confirmação, aplicação): não é uma recusa, repete-se mais tarde. */
const LOCK_409 = /a ser aplicad|a ser confirmad|está a ser (alterad|confirmad|aplicad)|tente dentro de/i

/** Falha que se repete (rede, servidor, tranca, sessão) vs recusa (vai para «Não gravadas»). */
export function isRetryable(error: unknown): boolean {
  const e = (error ?? {}) as WithStatus
  const status = typeof e.status === "number" ? e.status : null
  if (status === null) return true
  if (status >= 500 || status === 408 || status === 429 || status === 401) return true
  if (status === 409 && LOCK_409.test(String(e.message ?? ""))) return true
  return false
}

export interface PendingQueue<P, R> {
  items(): PendingItem<P>[]
  rejected(): RejectedItem<P>[]
  enqueue(key: string, payload: P): Promise<SendOutcome<R>>
  flush(): void
  retryRejected(key: string): Promise<SendOutcome<R>>
  discardRejected(key: string): void
}

export function createPendingQueue<P, R>(opts: {
  storageKey: string
  send: (payload: P) => Promise<R>
  onSent?: (result: R, payload: P) => void
  onChange?: () => void
  storage?: QueueStorage
  /** Um envio de cada vez. */
  serial?: boolean
}): PendingQueue<P, R> {
  const storage = opts.storage ?? browserStorage()
  const rejectedKey = `${opts.storageKey}.rejected`
  const load = <T,>(k: string): T[] => {
    try {
      const v = JSON.parse(storage.get(k) ?? "[]")
      return Array.isArray(v) ? v : []
    } catch {
      return []
    }
  }
  let items = load<PendingItem<P>>(opts.storageKey)
  let rejected = load<RejectedItem<P>>(rejectedKey)
  const inFlight = new Set<string>()
  let chain: Promise<unknown> = Promise.resolve()

  const persist = () => {
    if (items.length === 0) storage.remove(opts.storageKey)
    else storage.set(opts.storageKey, JSON.stringify(items))
    if (rejected.length === 0) storage.remove(rejectedKey)
    else storage.set(rejectedKey, JSON.stringify(rejected))
    opts.onChange?.()
  }

  const run = async (item: PendingItem<P>): Promise<SendOutcome<R>> => {
    try {
      const result = await opts.send(item.payload)
      items = items.filter((x) => x.key !== item.key)
      persist()
      opts.onSent?.(result, item.payload)
      return { ok: true, result }
    } catch (error) {
      if (isRetryable(error)) {
        items = items.map((x) => (x.key === item.key ? { ...x, failed: true } : x))
        persist()
        return { ok: false, queued: true }
      }
      const reason = (error as Error)?.message || "Recusada"
      items = items.filter((x) => x.key !== item.key)
      rejected = [...rejected.filter((x) => x.key !== item.key), { key: item.key, payload: item.payload, reason, at: new Date().toISOString() }]
      persist()
      return { ok: false, queued: false, error: error as Error }
    }
  }

  const attempt = (item: PendingItem<P>): Promise<SendOutcome<R>> => {
    if (inFlight.has(item.key)) return Promise.resolve({ ok: false, queued: true })
    inFlight.add(item.key)
    const go = () => run(item).finally(() => inFlight.delete(item.key))
    if (!opts.serial) return go()
    const p = chain.then(go, go)
    chain = p.catch(() => undefined)
    return p
  }

  return {
    items: () => items,
    rejected: () => rejected,
    enqueue(key, payload) {
      let item = items.find((x) => x.key === key)
      if (!item) {
        item = { key, payload, failed: false, at: new Date().toISOString() }
        items = [...items, item]
        rejected = rejected.filter((x) => x.key !== key)
        persist()
      }
      return attempt(item)
    },
    flush() {
      for (const item of items) void attempt(item)
    },
    retryRejected(key) {
      const r = rejected.find((x) => x.key === key)
      if (!r) return Promise.resolve({ ok: false, queued: true } as SendOutcome<R>)
      rejected = rejected.filter((x) => x.key !== key)
      const item = { key: r.key, payload: r.payload, failed: false, at: new Date().toISOString() }
      items = [...items, item]
      persist()
      return attempt(item)
    },
    discardRejected(key) {
      rejected = rejected.filter((x) => x.key !== key)
      persist()
    },
  }
}
